import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "../data/site";
import { Reveal, SectionLabel } from "../components/Reveal";
import { useLang } from "../i18n";

const CHAPTERS = [
  {
    num: "01",
    title: "La storia",
    title_en: "The story",
    text: "Bella è una pizzeria storica di Acireale, un nome che generazioni di acesi portano nel cuore. Dopo anni di attesa, ha riaperto in una nuova location in Via Vittorio Emanuele II: stessa anima, stesso forno, stessa pizza che i clienti ricordavano da bambini. Per molti, il primo morso è stato un tuffo nel passato.",
    text_en: "Bella is a historic pizzeria in Acireale, a name generations of locals hold dear. After years of waiting, it has reopened in a new location on Via Vittorio Emanuele II: same soul, same oven, same pizza that customers remembered from childhood. For many, the first bite was a trip back in time.",
  },
  {
    num: "02",
    title: "La filosofia",
    title_en: "The philosophy",
    text: "Poche regole, nessuna scorciatoia: fare la pizza come si è sempre fatta, con la cura di chi questo mestiere lo ha imparato in famiglia. Oggi la tradizione di Pippo Bella continua con il figlio Carmelo e il nipote Pippo Bella Jr., che porta avanti la stessa arte artigianale di un tempo.",
    text_en: "Few rules, no shortcuts: making pizza the way it has always been made, with the care of those who learned this craft in the family. Today Pippo Bella's tradition lives on with his son Carmelo and his grandson Pippo Bella Jr., carrying forward the same artisan craft as back then.",
  },
  {
    num: "03",
    title: "L'impasto",
    title_en: "The dough",
    text: "Farine selezionate e la possibilità di scegliere l'impasto con Farina di Timilia o Integrale. Il risultato è una pasta friabile, croccante al punto giusto e sorprendentemente leggera: una pizza intera si mangia volentieri, e si digerisce senza pensieri.",
    text_en: "Selected flours, with the option of a Timilia flour or whole wheat dough. The result is a crumbly base, crisp just right and surprisingly light: a whole pizza is a pleasure to eat, and easy to digest.",
  },
  {
    num: "04",
    title: "La lievitazione",
    title_en: "The leavening",
    text: "Il tempo è l'ingrediente che non si compra. La nostra lunga lievitazione rende l'impasto croccante, profumato e digeribile. È il motivo per cui la pizza di Bella si riconosce al primo assaggio: è proprio quella di una volta.",
    text_en: "Time is the ingredient you cannot buy. Our long leavening makes the dough crisp, fragrant and digestible. It is why Bella's pizza is recognisable at first bite: it is exactly the one from back then.",
  },
  {
    num: "05",
    title: "Il forno",
    title_en: "The oven",
    text: "Cottura in forno a pietra, come si faceva una volta: cornicione dorato e base sottile ma mai molle. Dal forno alla tavola in pochi secondi, perché la pizza va mangiata appena sfornata, ancora fumante.",
    text_en: "Baked in a stone oven, the way it used to be done: golden crust and a thin base that is never soft. From the oven to your table in seconds, because pizza must be eaten fresh out of the oven, still steaming.",
  },
  {
    num: "06",
    title: "Gli ingredienti",
    title_en: "The ingredients",
    text: "Partendo dalla salsa di pomodoro, selezioniamo con cura ogni ingrediente: dalla mozzarella Sole al pistacchio, dalla mortadella alla burrata, fino al pepato fresco, alla ricotta salata e al Grana Padano DOP. Materie prime autentiche e di qualità, scelte privilegiando il più possibile le eccellenze del territorio siciliano. E anche nel calice raccontiamo la nostra terra, con vini rigorosamente dell'isola, dall'Etna a Marsala fino a Mazara del Vallo.",
    text_en: "Starting from the tomato sauce, we carefully select every ingredient: from Sole mozzarella to pistachio, from mortadella to burrata, to fresh pepato, salted ricotta and Grana Padano PDO. Authentic, quality ingredients, chosen by favouring Sicilian excellence whenever possible. And in the glass we tell the story of our land too, with wines strictly from the island, from Etna to Marsala and Mazara del Vallo.",
  },
];

export default function ChiSiamo() {
  const { t, lang } = useLang();
  return (
    <main className="pt-20" data-testid="chi-siamo-page">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-20">
        <Reveal>
          <SectionLabel number="·">{t("about.label")}</SectionLabel>
          <h1 className="font-display uppercase leading-[0.9] tracking-tight text-6xl sm:text-8xl text-foreground">
            {t("about.l1")} <span className="text-outline-lime">{t("about.l2")}</span>
          </h1>
        </Reveal>
      </section>

      <section className="relative">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end pb-20">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="relative">
                <img src={IMAGES.chef} alt="Il pizzaiolo di Bella" className="relative w-full aspect-[4/5] sm:aspect-[4/3] object-cover rounded-sm" loading="lazy" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <p className="text-xl sm:text-2xl text-foreground leading-relaxed font-light">
                {t("about.q1")}<span className="text-neon">{t("about.q2")}</span>{t("about.q3")}
              </p>
              <p className="text-fog mt-4 text-sm tracking-[0.2em] uppercase font-head">{t("about.quoteBy")}</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.num} delay={0.05}>
            <div className={`grid grid-cols-1 lg:grid-cols-12 gap-6 py-14 border-t border-neon/15 ${i === CHAPTERS.length - 1 ? "border-b" : ""}`} data-testid={`chapter-${c.num}`}>
              <div className="lg:col-span-3">
                <span className="font-display text-6xl sm:text-7xl text-outline-lime">{c.num}</span>
              </div>
              <div className="lg:col-span-3">
                <h2 className="font-head font-bold uppercase tracking-tight text-3xl sm:text-4xl text-foreground">
                  {lang === "en" ? c.title_en : c.title}
                </h2>
              </div>
              <div className="lg:col-span-5 lg:col-start-8">
                <p className="text-fog leading-relaxed text-lg">{lang === "en" ? c.text_en : c.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <Reveal>
          <div className="relative overflow-hidden rounded-sm">
            <img src={IMAGES.pistachio} alt="Pizza Bella con burrata e pistacchio" loading="lazy" style={{ objectPosition: "center" }} className="w-full aspect-[16/8] object-cover brightness-105 saturate-110" />
            <div className="absolute inset-0 bg-ink/50" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
              <h2 className="font-display uppercase text-4xl sm:text-6xl text-foreground leading-none">
                {t("about.cta1")} <span className="text-neon">{t("about.cta2")}</span>
              </h2>
              <Link to="/prenota" data-testid="chi-siamo-cta-prenota"
                className="mt-8 inline-flex items-center gap-2 bg-neon text-ink font-head font-bold tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-transform duration-300 hover:scale-105">
                {t("about.book")} <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

    </main>
  );
}
